import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { IPostModel, IReplyCommentParams } from '../../models';
import DialogComponent from '../dialog/dialog.component';
import Slider, { Settings } from 'react-slick';
import {
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Send,
  Smile,
} from 'lucide-react';
import { LazyLoadImage } from '../lazy-load-image';
import Avatar from '../avatar/avatar.component';
import Nametag from '../cosmetics/nametag.component';
import { Link, useNavigate } from 'react-router-dom';
import { useComment } from '../../hooks/post/useComment';
import { useReplyComment } from '../../hooks/post/useReplyComment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { postService } from '../../services/post.service';
import Loading from '../loading/loading.component';
import _get from 'lodash/get';
import { sendCommentOfPost } from '../../api/posts/fetch-post.api';
import { useUserProfile } from '../../hooks/use-user-profile';
import classNames from 'classnames';
import { HiThumbDown, HiThumbUp } from 'react-icons/hi';
import InfiniteScroll from 'react-infinite-scroll-component';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import PostActionsComponent from '../post/post-actions/post-actions.component';
import PostMenu from '../post/post-menu.component';
import { isEmojiOnly } from '../../utils/emoji';

dayjs.extend(utc);

interface PreviewPostDialogProps {
  post: IPostModel;
  visible: boolean;
  initialSlide: number;
  onclose: () => void;
}

const PreviewPostDialog: React.FC<PreviewPostDialogProps> = ({
  visible,
  post,
  initialSlide,
  onclose,
}) => {
  const sliderRef = useRef<Slider>(null);
  const [pageNumberComment, setPageNumberComment] = useState(1);
  const [pageNumberReplyComment, setPageNumberReplyComment] = useState(1);
  const [commentValue, setCommentValue] = useState('');
  const [replyCommentValue, setReplyCommentValue] = useState('');
  const [isShowReply, setIsShowReply] = useState(false);
  const [replyCommentId, setReplyCommentId] = useState('');
  const [isReplyComment, setIsReplyComment] = useState(false);
  const [commentId, setCommentId] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showReplyEmojiPicker, setShowReplyEmojiPicker] = useState(false);
  const [showReplyEmojiPickerOnTop, setShowReplyEmojiPickerOnTop] =
    useState(true);
  const navigate = useNavigate();
  const replyInputRef = useRef<HTMLDivElement>(null);

  const { profile } = useUserProfile();
  const userId = useMemo(() => profile?.userId, [profile]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.emoji-picker-container')) {
        setShowEmojiPicker(false);
        setShowReplyEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (replyInputRef.current) {
      const rect = replyInputRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      setShowReplyEmojiPickerOnTop(spaceBelow < 350); // 350px là chiều cao ước tính của emoji picker
    }
  }, [isReplyComment]);
  const profileUser = useSelector(
    (state: RootState) => state.features.auth.profile
  );

  const {
    data: commentList,
    loading: loadingComment,
    refreshCommentClient,
    getCommentWhenComment,
    hasMore,
    getComment,
  } = useComment({
    pageNumber: pageNumberComment,
    pageSize: 10,
    postId: _get(post, 'id', ''),
    visible: true,
  });

  const { data: replyCommentList, getReplyCommentWhenReply } = useReplyComment({
    pageNumber: pageNumberReplyComment,
    commentId: replyCommentId,
    visible: isShowReply,
  });

  const images = useMemo(
    () => post?.postImageResponses?.map((image) => image) ?? [],
    [post]
  );

  const settings = useMemo<Settings>(
    () => ({
      dots: false,
      infinite: post?.postImageResponses?.length > 1,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      initialSlide,
    }),
    [initialSlide]
  );

  const handleSendComment = useCallback(async () => {
    if (!commentValue?.trim()) return;

    const res = await sendCommentOfPost({
      userId,
      postId: post?.id,
      content: commentValue,
    });
    if (res) {
      await getCommentWhenComment();
      setCommentValue('');
    }
  }, [commentValue, userId, post?.id, getCommentWhenComment]);

  const handleCreateReplyComments = useCallback(
    (id: string) => {
      setIsReplyComment((prev) => {
        if (!commentId) return true;
        if (id === commentId) return !prev;
        return true;
      });
      setCommentId(id);
    },
    [commentId]
  );

  const handleSendRelyComment = useCallback(
    async (commentId: string) => {
      if (!replyCommentValue?.trim()) return;

      setReplyCommentId(commentId);

      try {
        const payload: IReplyCommentParams = {
          content: replyCommentValue,
          replyToCommentId: commentId,
        };

        await postService.replyComment(payload);
        setIsShowReply(true);
        setReplyCommentValue('');

        if (isShowReply) {
          await getReplyCommentWhenReply(commentId);
        }

        await refreshCommentClient();
      } catch (error) {
        console.error('Error sending reply comment:', error);
      }
    },
    [
      replyCommentValue,
      isShowReply,
      getReplyCommentWhenReply,
      refreshCommentClient,
    ]
  );

  const toggleLike = useCallback(
    async (id: string, type: number) => {
      await postService.likeComment({ commentId: id, type });
      await refreshCommentClient();
    },
    [refreshCommentClient]
  );

  const onEmojiSelect = useCallback((emoji: any) => {
    setCommentValue((prev) => prev + emoji.native);
    setShowEmojiPicker(false);
  }, []);

  const onReplyEmojiSelect = useCallback((emoji: any) => {
    setReplyCommentValue((prev) => prev + emoji.native);
    setShowReplyEmojiPicker(false);
  }, []);

  const fetchMoreData = useCallback(() => {
    if (!loadingComment && hasMore) {
      setPageNumberComment((prev) => prev + 1);
      // getComment({
      //   pageNumber: pageNumberComment + 1,
      //   pageSize: 10,
      //   postId: post.id,
      //   pagingState: '',
      // });
    }
  }, [pageNumberComment, loadingComment, hasMore, post.id, getComment]);

  if (!visible) return null;

  const hasImage = !_.isEmpty(post?.postImageResponses);
  const hasVideo =
    !_.isEmpty(post?.postVideoResponses) && !!post?.postVideoResponses?.[0]?.url;

  return (
    <div>
      <DialogComponent
        visible={visible}
        onClose={onclose}
        size={hasImage || hasVideo ? 'full' : 'lg'}
        customSize="w-full max-w-[1392px]"
      >
        <div className="flex w-full lg:h-[85vh] flex-col sm:flex-col lg:flex-row ">
          {hasImage && (
            <>
              {/* Left side - Image slider */}
              <div className=" w-[100%] lg:w-[60%] bg-zinc-900 flex items-center relative rounded-l-lg overflow-hidden">
                {post?.postImageResponses?.length > 1 && (
                  <button
                    className="text-white bg-white/30 absolute left-4 z-10 hover:bg-white/10 rounded-full p-2"
                    onClick={() => sliderRef.current?.slickPrev()}
                  >
                    <ChevronLeft size={24} />
                  </button>
                )}
                {post?.postImageResponses?.length > 1 ? (
                  <Slider
                    ref={sliderRef}
                    {...settings}
                    className="post-image-slider w-full h-[80vh] lg:h-full"
                  >
                    {post?.postImageResponses?.map((image, index) => (
                      <div
                        key={image.imageId}
                        className="relative outline-none w-full h-full flex items-center justify-center overflow-hidden bg-zinc-900"
                      >
                        <div
                          aria-hidden
                          className="absolute inset-0 scale-110 bg-cover bg-center opacity-50 blur-2xl"
                          style={{ backgroundImage: `url(${image.url})` }}
                        />
                        <img
                          className="absolute inset-0 m-auto z-[1] max-h-full max-w-full object-contain"
                          src={image.url}
                          alt={`Slide ${index + 1}`}
                        />
                      </div>
                    ))}
                  </Slider>
                ) : (
                  post?.postImageResponses?.map((image, index) => (
                    <div
                      key={image.imageId}
                      className="relative w-full h-[85vh] rounded-l-lg overflow-hidden flex justify-center items-center bg-zinc-900"
                    >
                      {/* Backdrop mờ từ chính ảnh để lấp khoảng trống 2 bên (không còn dải đen) */}
                      <div
                        aria-hidden
                        className="absolute inset-0 scale-110 bg-cover bg-center opacity-50 blur-2xl"
                        style={{ backgroundImage: `url(${image.url})` }}
                      />
                      <img
                        src={image.url}
                        alt={`Slide ${index + 1}`}
                        className="relative z-[1] max-h-full max-w-full object-contain"
                      />
                    </div>
                  ))
                )}
                {post?.postImageResponses?.length > 1 && (
                  <button
                    className="text-white bg-white/30 absolute right-4 z-10 hover:bg-white/10 rounded-full p-2"
                    onClick={() => sliderRef.current?.slickNext()}
                  >
                    <ChevronRight size={24} />
                  </button>
                )}
              </div>
            </>
          )}

          {/* Left side - Video player (bài video: trước đây không render nên chỉ thấy text) */}
          {!hasImage && hasVideo && (
            <div className="w-[100%] lg:w-[60%] bg-black flex items-center justify-center relative rounded-l-lg">
              <video
                src={post?.postVideoResponses?.[0]?.url}
                controls
                playsInline
                className="w-full h-full max-h-[85vh] object-contain"
              >
                <track kind="captions" src="" label="captions" />
                Trình duyệt của bạn không hỗ trợ phát video.
              </video>
            </div>
          )}

          <div
            style={{ borderRadius: '8px' }}
            className={`bg-white dark:bg-zinc-900 flex flex-col overflow-hidden ${
              hasImage || hasVideo ? 'w-[100%] lg:w-[40%]' : 'w-full'
            }`}
          >
            <div className="p-6 pb-3 shrink-0">
              {/* Header */}
              <div className=" dark:border-zinc-700 flex items-center justify-between">
                <div className="flex flex-col items-center gap-3  w-full">
                  <div className="flex flex-col gap-2 w-full">
                    <div className="flex justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <Link to={`/user/${post.username}`}>
                          <Avatar
                            avatar={post.userAvatar}
                            size="sm"
                            label={post.userFullName}
                            userId={post.userId}
                          />
                        </Link>
                        <div className="flex flex-col">
                          <Nametag userId={post.userId} banner compact>
                            <Link
                              to={`/user/${post.username}`}
                              className="font-medium text-slate-800 dark:text-white"
                            >
                              {post.userFullName}
                            </Link>
                          </Nametag>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <span className="text-branding font-semibold">
                              {_.get(post, 'categoryName', '')}
                            </span>
                            <span>•</span>
                            <span>
                              {dayjs.utc(post?.createdAt).fromNow()}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <PostMenu
                          post={post}
                          userId={userId || ''}
                          removePost={() => onclose()}
                          setIsHiddenPost={() => onclose()}
                        />
                        <svg
                          className=" cursor-pointer"
                          onClick={() => onclose()}
                          width="40"
                          height="40"
                          viewBox="0 0 40 40"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect width="40" height="40" rx="20" fill="#EAEFFA" />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M27.2803 12.7197C27.5732 13.0126 27.5732 13.4874 27.2803 13.7803L13.7803 27.2803C13.4874 27.5732 13.0126 27.5732 12.7197 27.2803C12.4268 26.9874 12.4268 26.5126 12.7197 26.2197L26.2197 12.7197C26.5126 12.4268 26.9874 12.4268 27.2803 12.7197Z"
                            fill="#657384"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M12.7197 12.7197C13.0126 12.4268 13.4874 12.4268 13.7803 12.7197L27.2803 26.2197C27.5732 26.5126 27.5732 26.9874 27.2803 27.2803C26.9874 27.5732 26.5126 27.5732 26.2197 27.2803L12.7197 13.7803C12.4268 13.4874 12.4268 13.0126 12.7197 12.7197Z"
                            fill="#657384"
                          />
                        </svg>
                      </div>
                    </div>

                    <p className="text-[15px] text-zinc-800 leading-snug dark:text-zinc-50 line-clamp-2">
                      {post?.content}
                    </p>
                    {post?.hashtags && (
                      <div className="flex flex-wrap gap-1">
                        {post.hashtags.split(' ').map((tag, index) => (
                          <span
                            key={index}
                            className="text-violet-800  leading-4 dark:text-violet-500 text-sm hover:underline cursor-pointer"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="border-b border-b-[#E5E7EB] dark:border-b-slate-700">
                <PostActionsComponent
                  post={post as IPostModel}
                  isPostUser={post.userId === profileUser?.userId || false}
                  profileUser={!!profileUser}
                  customClassName="!px-0 !py-2"
                />
              </div>
            </div>

            {/* Comment list — vùng cuộn riêng (ô nhập nằm trên qua order-1) */}
            {/* Padding trái dời vào TRONG InfiniteScroll: vùng cuộn (.infinite-scroll-component) cắt overflow-x,
                mà khung avatar Sử Thi/cosmetic lồi ~10px sang trái → bị cắt. Cho padding nằm trong vùng cuộn
                để khung có chỗ hiện đủ (giữ nguyên khoảng cách nhìn thấy). */}
            <div id="scrollableDiv" className="order-2 flex-1 min-h-0 overflow-y-auto pr-6 py-3">
                <InfiniteScroll
                  className="pl-6"
                  dataLength={commentList.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  loader={<Loading />}
                  scrollableTarget="scrollableDiv"
                >
                  {commentList.map((comment) => (
                    <div
                      key={comment.id}
                      className="py-4 border-b border-gray-100 dark:border-zinc-800"
                    >
                      <div className="flex gap-3">
                        <Link to={`/user/${comment.username}`}>
                          <Avatar
                            avatar={comment.avatar}
                            size="xs"
                            label={comment.userFullName}
                            userId={comment.userId}
                          />
                        </Link>
                        <div className="flex-1">
                          <div className="rounded-2xl pb-3">
                            <Nametag userId={comment.userId} banner compact>
                              <Link
                                to={`/user/${comment.username}`}
                                className="font-medium text-slate-800 dark:text-white"
                              >
                                {comment.userFullName}
                              </Link>
                            </Nametag>
                            <p
                              className={`text-[#111827] dark:text-slate-300 mt-1 break-words whitespace-pre-wrap ${
                                isEmojiOnly(comment.content)
                                  ? 'text-[30px] leading-tight'
                                  : 'text-[15px]'
                              }`}
                            >
                              {comment.content}
                            </p>
                          </div>

                          <div className="flex items-center gap-4 mt-2 text-xs">
                            {!comment.currentUserHasUnlike && (
                              <button
                                className={classNames('font-medium', {
                                  'text-blue-500': comment.currentUserHasLike,
                                  'text-slate-500': !comment.currentUserHasLike,
                                })}
                                onClick={() => toggleLike(comment.id, 1)}
                              >
                                <svg
                                  width="32"
                                  height="33"
                                  viewBox="0 0 32 33"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <rect
                                    y="0.5"
                                    width="32"
                                    height="32"
                                    rx="16"
                                    fill="#EAEFFA"
                                  />
                                  <path
                                    d="M13.5933 20.8265V14.0532C13.5933 13.7865 13.6733 13.5265 13.8199 13.3065L15.6399 10.5998C15.9266 10.1665 16.6399 9.85982 17.2466 10.0865C17.8999 10.3065 18.3333 11.0398 18.1933 11.6932L17.8466 13.8732C17.8199 14.0732 17.8733 14.2532 17.9866 14.3932C18.0999 14.5198 18.2666 14.5998 18.4466 14.5998H21.1866C21.7133 14.5998 22.1666 14.8132 22.4333 15.1865C22.6866 15.5465 22.7333 16.0132 22.5666 16.4865L20.9266 21.4798C20.7199 22.3065 19.8199 22.9798 18.9266 22.9798H16.3266C15.8799 22.9798 15.2533 22.8265 14.9666 22.5398L14.1133 21.8798C13.7866 21.6332 13.5933 21.2398 13.5933 20.8265Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M11.4733 12.7534H10.7866C9.75325 12.7534 9.33325 13.1534 9.33325 14.1401V20.8468C9.33325 21.8334 9.75325 22.2334 10.7866 22.2334H11.4733C12.5066 22.2334 12.9266 21.8334 12.9266 20.8468V14.1401C12.9266 13.1534 12.5066 12.7534 11.4733 12.7534Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </button>
                            )}

                            {!comment.currentUserHasLike && (
                              <button
                                className={classNames('font-medium', {
                                  'text-red-500': comment.currentUserHasUnlike,
                                  'text-slate-500':
                                    !comment.currentUserHasUnlike,
                                })}
                                onClick={() => toggleLike(comment.id, -1)}
                              >
                                <svg
                                  width="32"
                                  height="33"
                                  viewBox="0 0 32 33"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <rect
                                    y="0.5"
                                    width="32"
                                    height="32"
                                    rx="16"
                                    fill="#EAEFFA"
                                  />
                                  <path
                                    d="M18.4066 12.1665V18.9398C18.4066 19.2065 18.3266 19.4665 18.1799 19.6865L16.3599 22.3932C16.0733 22.8265 15.3599 23.1332 14.7533 22.9065C14.0999 22.6865 13.6666 21.9532 13.8066 21.2998L14.1533 19.1198C14.1799 18.9198 14.1266 18.7398 14.0133 18.5998C13.8999 18.4732 13.7333 18.3932 13.5533 18.3932H10.8133C10.2866 18.3932 9.83326 18.1799 9.5666 17.8065C9.31326 17.4465 9.2666 16.9798 9.43326 16.5065L11.0733 11.5132C11.2799 10.6865 12.1799 10.0132 13.0733 10.0132H15.6733C16.1199 10.0132 16.7466 10.1665 17.0333 10.4532L17.8866 11.1132C18.2133 11.3665 18.4066 11.7532 18.4066 12.1665Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M20.5266 20.2399H21.2132C22.2466 20.2399 22.6666 19.8399 22.6666 18.8533V12.1533C22.6666 11.1666 22.2466 10.7666 21.2132 10.7666H20.5266C19.4932 10.7666 19.0732 11.1666 19.0732 12.1533V18.8599C19.0732 19.8399 19.4932 20.2399 20.5266 20.2399Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </button>
                            )}

                            <button
                              className=" bg-[#EAEFFA] p-2 rounded-[500px] text-[#657384] font-semibold text-sm leading-4"
                              onClick={() =>
                                handleCreateReplyComments(comment.id)
                              }
                            >
                              Phản hồi
                            </button>

                            <span className="text-slate-500">
                              {dayjs.utc(comment.createdAt).local().fromNow()}
                            </span>

                            {comment.totalReply > 0 && (
                              <button
                                className="font-medium text-slate-500"
                                onClick={() => {
                                  setIsShowReply((prev) => !prev);
                                  setReplyCommentId(comment.id);
                                }}
                              >
                                {isShowReply && comment.id === replyCommentId
                                  ? 'Thu gọn'
                                  : `Xem ${comment.totalReply} phản hồi`}
                              </button>
                            )}
                          </div>

                          {isShowReply && comment.id === replyCommentId && (
                            <div className="mt-2 space-y-4">
                              {replyCommentList?.map((reply) => (
                                <div key={reply.id} className="flex gap-3">
                                  <Link to={`/user/${reply.username}`}>
                                    <Avatar
                                      avatar={reply.avatar}
                                      size="xs"
                                      label={reply.userFullName}
                                      userId={reply.userId}
                                    />
                                  </Link>
                                  <div className="flex-1">
                                    <div className="bg-gray-100 dark:bg-zinc-800 rounded-2xl px-4 py-2">
                                      <Nametag userId={reply.userId} banner compact>
                                        <Link
                                          to={`/user/${reply.username}`}
                                          className="font-medium text-slate-800 dark:text-white"
                                        >
                                          {reply.userFullName}
                                        </Link>
                                      </Nametag>
                                      <p
                                        className={`text-slate-600 dark:text-slate-300 mt-1 break-words whitespace-pre-wrap ${
                                          isEmojiOnly(reply.content)
                                            ? 'text-[30px] leading-tight'
                                            : 'text-[15px]'
                                        }`}
                                      >
                                        {reply.content}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-3 mt-2">
                                      <span className="text-xs text-slate-500">
                                        {dayjs
                                          .utc(reply.createdAt)
                                          .local()
                                          .fromNow()}
                                      </span>
                                      {/* Trả lời cho 1 reply: mở ô nhập của comment gốc, prefill @tên (thread phẳng) */}
                                      <button
                                        className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                        onClick={() => {
                                          setCommentId(comment.id);
                                          setIsReplyComment(true);
                                          setReplyCommentValue(
                                            `@${reply.userFullName || reply.username || ''} `
                                          );
                                        }}
                                      >
                                        Phản hồi
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {isReplyComment && commentId === comment.id && (
                            <div className="mt-4 flex items-center gap-2">
                              {profileUser && (
                                <Avatar
                                  avatar={profileUser.avatar}
                                  label={profileUser.username}
                                  size="sm"
                                />
                              )}
                              <div
                                ref={replyInputRef}
                                className="flex-1 relative"
                              >
                                <input
                                  type="text"
                                  placeholder="Viết phản hồi..."
                                  className="w-full h-10 bg-gray-100 dark:bg-zinc-800 rounded-full px-4 py-2 text-sm outline-none pr-10"
                                  value={replyCommentValue}
                                  onChange={(e) =>
                                    setReplyCommentValue(e.target.value)
                                  }
                                />
                                <button
                                  onClick={() =>
                                    setShowReplyEmojiPicker(
                                      !showReplyEmojiPicker
                                    )
                                  }
                                  className="absolute right-3 top-1/2 -translate-y-1/2"
                                >
                                  <Smile size={20} className="text-slate-500" />
                                </button>
                                {showReplyEmojiPicker && (
                                  <div
                                    className={classNames(
                                      'absolute z-[1] right-0 mb-2 emoji-picker-container',
                                      {
                                        'bottom-full':
                                          showReplyEmojiPickerOnTop,
                                        'top-full mt-2':
                                          !showReplyEmojiPickerOnTop,
                                      }
                                    )}
                                  >
                                    <Picker
                                      data={data}
                                      onEmojiSelect={onReplyEmojiSelect}
                                      emojiSize={30}
                                      emojiButtonSize={42}
                                      perLine={8}
                                    />
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() =>
                                  handleSendRelyComment(comment.id)
                                }
                                disabled={!replyCommentValue}
                                className={`${
                                  replyCommentValue
                                    ? 'text-blue-500 cursor-pointer'
                                    : 'text-[#657384]'
                                } `}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendRelyComment(comment.id);
                                  }
                                }}
                              >
                                <svg
                                  width="40"
                                  height="40"
                                  viewBox="0 0 40 40"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <rect
                                    width="40"
                                    height="40"
                                    rx="20"
                                    fill="#EAEFFA"
                                  />
                                  <path
                                    d="M24.1391 10.96L15.1091 13.96C9.03914 15.99 9.03914 19.3 15.1091 21.32L17.7891 22.21L18.6791 24.89C20.6991 30.96 24.0191 30.96 26.0391 24.89L29.0491 15.87C30.3891 11.82 28.1891 9.61004 24.1391 10.96ZM24.4591 16.34L20.6591 20.16C20.5091 20.31 20.3191 20.38 20.1291 20.38C19.9391 20.38 19.7491 20.31 19.5991 20.16C19.3091 19.87 19.3091 19.39 19.5991 19.1L23.3991 15.28C23.6891 14.99 24.1691 14.99 24.4591 15.28C24.7491 15.57 24.7491 16.05 24.4591 16.34Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </InfiniteScroll>
            </div>
            {/* Comment input — đưa lên trên (order-1), ngay dưới hàng like/donate */}
            <div className="p-3 border-b border-gray-200 dark:border-zinc-700 order-1 shrink-0">
              <div className="flex items-center gap-3">
                {profileUser && (
                  <Avatar
                    avatar={profileUser.avatar}
                    label={profileUser.username}
                    size="sm"
                  />
                )}
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Thêm bình luận..."
                    className="w-full h-10 bg-gray-100 dark:bg-zinc-800 rounded-full px-4 py-2 text-sm outline-none pr-10"
                    value={commentValue}
                    onChange={(e) => setCommentValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendComment();
                      }
                    }}
                  />
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <Smile size={20} className="text-slate-500" />
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute top-full right-0 mt-2 z-20 emoji-picker-container">
                      <Picker
                        data={data}
                        onEmojiSelect={onEmojiSelect}
                        emojiSize={30}
                        emojiButtonSize={42}
                        perLine={8}
                      />
                    </div>
                  )}
                </div>
                <button
                  onClick={handleSendComment}
                  className={`${
                    commentValue
                      ? 'text-blue-500 cursor-pointer'
                      : 'text-[#657384]'
                  } `}
                  disabled={!commentValue}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="40" height="40" rx="20" fill="#EAEFFA" />
                    <path
                      d="M24.1391 10.96L15.1091 13.96C9.03914 15.99 9.03914 19.3 15.1091 21.32L17.7891 22.21L18.6791 24.89C20.6991 30.96 24.0191 30.96 26.0391 24.89L29.0491 15.87C30.3891 11.82 28.1891 9.61004 24.1391 10.96ZM24.4591 16.34L20.6591 20.16C20.5091 20.31 20.3191 20.38 20.1291 20.38C19.9391 20.38 19.7491 20.31 19.5991 20.16C19.3091 19.87 19.3091 19.39 19.5991 19.1L23.3991 15.28C23.6891 14.99 24.1691 14.99 24.4591 15.28C24.7491 15.57 24.7491 16.05 24.4591 16.34Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </DialogComponent>
    </div>
  );
};

export default PreviewPostDialog;
